import { useId, type FormEvent } from "react";
import type { Client, ClientCreateInput } from "@/api/clients";
import { RequestError } from "@/components/RequestError";
import { Checkbox } from "@/components/ui/checkbox";
import { TextAreaField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

export type ClientFormValues = {
  code: string;
  legal_name: string;
  address: string;
  country_code: string;
  place_of_supply: string;
  cr_no: string;
  vat_no: string;
  payment_days: string;
  currency: string;
  agreement_ref: string;
  agreement_effective_date: string;
  notes: string;
  reminders_enabled: boolean;
};

export function clientFormValues(client?: Client | null): ClientFormValues {
  return {
    code: client?.code ?? "",
    legal_name: client?.legal_name ?? "",
    address: client?.address_lines.join("\n") ?? "",
    country_code: client?.country_code ?? "",
    place_of_supply: client?.place_of_supply ?? "",
    cr_no: client?.cr_no ?? "",
    vat_no: client?.vat_no ?? "",
    payment_days: String(client?.payment_days ?? 7),
    currency: client?.currency ?? "USD",
    agreement_ref: client?.agreement_ref ?? "",
    agreement_effective_date: client?.agreement_effective_date ?? "",
    notes: client?.notes ?? "",
    reminders_enabled: client?.reminders_enabled ?? true,
  };
}

export function toClientInput(values: ClientFormValues): ClientCreateInput {
  return {
    code: values.code.trim(),
    legal_name: values.legal_name.trim(),
    address_lines: values.address
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean),
    country_code: values.country_code.trim(),
    place_of_supply: values.place_of_supply.trim(),
    cr_no: values.cr_no.trim(),
    vat_no: values.vat_no.trim(),
    payment_days: Number.parseInt(values.payment_days, 10),
    currency: values.currency.trim(),
    agreement_ref: values.agreement_ref.trim(),
    agreement_effective_date: values.agreement_effective_date || null,
    notes: values.notes.trim(),
    reminders_enabled: values.reminders_enabled,
  };
}

type ClientFormProps = {
  mode: "create" | "edit";
  initial?: Client | null;
  submitLabel: string;
  onSubmit: (input: ClientCreateInput) => Promise<unknown>;
  onCancel?: () => void;
  onReload?: () => void;
};

export function ClientForm({
  mode,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  onReload,
}: ClientFormProps) {
  const id = useId();
  const { values, set } = useFormValues(clientFormValues(initial));
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() => onSubmit(toClientInput(values)));
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-code`}
          label="Code"
          value={values.code}
          onChange={(value) => set("code", value)}
          error={errors.code}
          hint={mode === "create" ? "Short permanent code, e.g. TWC" : "The client code cannot be changed"}
          disabled={mode === "edit"}
          autoCapitalize="characters"
          maxLength={16}
        />
        <TextField
          id={`${id}-country`}
          label="Country"
          value={values.country_code}
          onChange={(value) => set("country_code", value)}
          error={errors.country_code}
          hint="2-letter code, e.g. SA"
          autoCapitalize="characters"
          maxLength={2}
        />
        <TextField
          id={`${id}-legal-name`}
          label="Legal name"
          value={values.legal_name}
          onChange={(value) => set("legal_name", value)}
          error={errors.legal_name}
          className="md:col-span-2"
        />
        <TextAreaField
          id={`${id}-address`}
          label="Address"
          value={values.address}
          onChange={(value) => set("address", value)}
          error={errors.address_lines}
          hint="One line per row, up to 6 lines"
          rows={3}
          className="md:col-span-2"
        />
        <TextField
          id={`${id}-place-of-supply`}
          label="Place of supply"
          value={values.place_of_supply}
          onChange={(value) => set("place_of_supply", value)}
          error={errors.place_of_supply}
          hint="e.g. Kingdom of Saudi Arabia (outside India)"
          className="md:col-span-2"
        />
        <TextField
          id={`${id}-cr`}
          label="CR number"
          value={values.cr_no}
          onChange={(value) => set("cr_no", value)}
          error={errors.cr_no}
        />
        <TextField
          id={`${id}-vat`}
          label="VAT number"
          value={values.vat_no}
          onChange={(value) => set("vat_no", value)}
          error={errors.vat_no}
        />
        <TextField
          id={`${id}-payment-days`}
          label="Payment days"
          type="number"
          inputMode="numeric"
          min={0}
          max={365}
          value={values.payment_days}
          onChange={(value) => set("payment_days", value)}
          error={errors.payment_days}
        />
        <TextField
          id={`${id}-currency`}
          label="Currency"
          value={values.currency}
          onChange={(value) => set("currency", value)}
          error={errors.currency}
          hint="3-letter code, e.g. USD"
          autoCapitalize="characters"
          maxLength={3}
        />
        <TextField
          id={`${id}-agreement-ref`}
          label="Agreement reference"
          value={values.agreement_ref}
          onChange={(value) => set("agreement_ref", value)}
          error={errors.agreement_ref}
          hint="Printed in the invoice notes"
        />
        <TextField
          id={`${id}-agreement-date`}
          label="Agreement effective date"
          type="date"
          value={values.agreement_effective_date}
          onChange={(value) => set("agreement_effective_date", value)}
          error={errors.agreement_effective_date}
        />
        <TextAreaField
          id={`${id}-notes`}
          label="Notes"
          value={values.notes}
          onChange={(value) => set("notes", value)}
          error={errors.notes}
          rows={2}
          className="md:col-span-2"
        />
        <Checkbox
          id={`${id}-reminders`}
          label="E-mail payment reminders"
          checked={values.reminders_enabled}
          onChange={(value) => set("reminders_enabled", value)}
          hint="The daily job reminds this client's invoice contacts when an invoice is overdue"
          className="md:col-span-2"
        />
      </div>
      <FormActions saving={saving} submitLabel={submitLabel} onCancel={onCancel} />
    </form>
  );
}
